import React from 'react';
import { Accessibility, CheckCircle, AlertTriangle } from 'lucide-react';
import { ACCESSIBILITY_FILTERS, isVerifiableRequirement } from '../../data/hotelAccessibility';

const ACCESS_NEED_LABELS = {
  roll_in_shower:     'Roll-in shower',
  grab_rails:         'Grab rails / bathroom support',
  bath_hoist:         'Bath hoist / shower chair',
  ground_floor:       'Ground floor or lift access',
  wide_doorways:      'Wide doorways / turning circle (≥ 900mm)',
  hearing_loop:       'Hearing loop / visual fire alarm',
  accessible_parking: 'Accessible parking at property',
  carer_bed:          'Carer / attendant bed in same room',
  adjustable_bed:     'Height-adjustable or profiling bed',
  no_steps:           'No steps at entry / throughout',
};

/**
 * The traveller's recorded access needs, and the subset of them the hotel API can actually speak
 * to, shown above the hotel results.
 *
 * Lives in its own file rather than inside HotelBookingFlow because it is the one piece of that
 * 2,000-line component that genuinely has to be right for a specific group of people, and it was
 * unreachable by any test while it was buried behind a live hotel search.
 *
 * Always rendered. The filters are useful on any booking, not only where the trip happened to
 * record a requirement — it simply drops to a quiet neutral panel when there is nothing specific
 * to carry through, so it does not shout on every ordinary search.
 */
export default function HotelAccessibilityPanel({
  accessNeeds = [], accessNotes = '', selected = [], counts = {},
  dataAvailable = true, onToggle,
}) {
  // Amber when this trip actually records a requirement, neutral when the panel is just offering
  // the filters — prominence should track how much the traveller's needs are riding on it.
  const hasRecordedNeeds = accessNeeds.length > 0;
  const accentText  = hasRecordedNeeds ? 'text-amber-700' : 'text-slate-600';
  const bodyText    = hasRecordedNeeds ? 'text-amber-900' : 'text-slate-700';
  const dividerLine = hasRecordedNeeds ? 'border-amber-200' : 'border-slate-200';
  const unverifiable = accessNeeds.filter(k => !isVerifiableRequirement(k));

  return (
    <section aria-labelledby="access-heading"
      className={`border rounded-lg p-3 space-y-3 ${
        hasRecordedNeeds ? 'border-amber-300 bg-amber-50' : 'border-slate-200 bg-white'}`}>
      <div className="flex items-start gap-2">
        <Accessibility size={16} aria-hidden="true" className={`mt-0.5 shrink-0 ${accentText}`} />
        <div>
          <h3 id="access-heading" className={`text-sm font-bold ${bodyText}`}>
            {hasRecordedNeeds ? 'Accessibility requirements for this trip' : 'Accessibility'}
          </h3>
          <p className={`text-xs mt-0.5 ${bodyText}`}>
            Hotel facilities are supplier-reported and coarse. A match narrows the list — it does
            not confirm the room is suitable. <strong>Confirm every requirement with the property
            before booking.</strong>
          </p>
        </div>
      </div>

      {hasRecordedNeeds && (
        <ul className="space-y-1">
          {accessNeeds.map(key => {
            const checkable = isVerifiableRequirement(key);
            return (
              <li key={key} className={`text-xs flex items-start gap-1.5 ${bodyText}`}>
                {checkable
                  ? <CheckCircle size={12} aria-hidden="true" className={`mt-0.5 shrink-0 ${accentText}`} />
                  : <AlertTriangle size={12} aria-hidden="true" className={`mt-0.5 shrink-0 ${accentText}`} />}
                <span>
                  {ACCESS_NEED_LABELS[key] || key}
                  {!checkable && (
                    <span className="font-semibold"> — cannot be checked automatically; ask the property</span>
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      )}

      {accessNotes && (
        <p className={`text-xs border-t pt-2 ${bodyText} ${dividerLine}`}>
          <span className="font-semibold">Notes: </span>{accessNotes}
        </p>
      )}

      <div className={`pt-2 ${hasRecordedNeeds || accessNotes ? `border-t ${dividerLine}` : ''}`}>
        <h4 className={`text-[11px] font-semibold mb-1.5 ${bodyText}`} id="access-filter-heading">
          Filter by reported facilities
        </h4>

        {/* Searching by hotel NAME builds its one result by hand with no facility ids on it, and
            some city results come back bare too. Filtering would then exclude everything while
            every chip read (0) — which on this screen reads as "no accessible hotels" rather than
            "no data", the worst possible misreading. Say which it is instead. */}
        {!dataAvailable ? (
          <p className={`text-[11px] ${bodyText}`}>
            This search returned no facility information, so there is nothing to filter on — it does
            not mean these properties lack accessible facilities. Check with the property directly.
          </p>
        ) : (
          <>
            <div className="flex flex-wrap gap-1.5" role="group" aria-labelledby="access-filter-heading">
              {ACCESSIBILITY_FILTERS.map(f => {
                const on = selected.includes(f.key);
                const n  = counts[f.key] ?? 0;
                return (
                  <button
                    key={f.key}
                    type="button"
                    aria-pressed={on}
                    onClick={() => onToggle?.(f.key)}
                    className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                      on ? (hasRecordedNeeds ? 'bg-amber-700 text-white border-amber-700'
                                             : 'bg-slate-700 text-white border-slate-700')
                         : (hasRecordedNeeds ? 'bg-white text-amber-900 border-amber-300 hover:bg-amber-100'
                                             : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-100')}`}
                  >
                    {f.label} <span className="opacity-70">({n} of the results)</span>
                  </button>
                );
              })}
            </div>
            {unverifiable.length > 0 && (
              <p className={`text-[11px] mt-1.5 ${bodyText}`}>
                {unverifiable.length} recorded requirement{unverifiable.length !== 1 ? 's have' : ' has'} no
                equivalent in the hotel data and {unverifiable.length !== 1 ? 'are' : 'is'} not filterable.
              </p>
            )}
          </>
        )}
      </div>
    </section>
  );
}
